import { useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useTranslation } from "react-i18next";
import ChartEditorPanel from "../ChartEditorPanel";
import ChartSlicesEditor from "../ChartSlicesEditor";
import IndexTimeFieldAutocomplete from "../IndexTimeFieldAutocomplete";
import { AppAutocomplete, AppButton } from "../form";
import { AppFormField, AppFormGrid } from "../layout";
import { GRID_FULL, chartOptionInputSx } from "../../lib/ui/formLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import {
  buildWidgetChartMapping,
  mappingToJson,
  parseWidgetChartMapping,
  type WidgetChartMapping,
} from "../../utils/chartMappingBuilder";
import TableColumnsEditor from "../TableColumnsEditor";
import {
  buildTableMappingJson,
  createCustomTableColumn,
  fieldOptionToColumn,
  findTableResultFieldOptions,
  parseTableMapping,
  type TableColumnMapping,
} from "../../utils/tableMappingBuilder";
import {
  analyzeOpenSearchTableResponse,
  resolveResultSourceId,
  resultSourceToRowSource,
  type OpenSearchTableAnalysis,
  type TableResultFieldOption,
  type TableResultSource,
} from "../../utils/openSearchTableAnalysis";
import type { WidgetOutput } from "../../types/widget";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

type OutputFormat = NonNullable<WidgetOutput["format"]>;

type Props = {
  outputFormat: OutputFormat;
  widgetName?: string;
  primaryIndex: string;
  timeField?: string;
  mappingText: string;
  onMappingTextChange: (text: string) => void;
  onTimeFieldChange?: (timeField: string) => void;
  tableAnalysis?: OpenSearchTableAnalysis | null;
  probeLoading?: boolean;
  probeError?: string | null;
  onRefreshProbe?: () => void;
  disabled?: boolean;
  /** Soften OpenSearch-only index/timeField requirements for live adaptor widgets. */
  adaptorSource?: boolean;
};

function sourceLabel(source: TableResultSource, t: (key: string, opts?: Record<string, unknown>) => string) {
  if (source.kind === "aggregation") {
    return t("visualEditor.resultSourceAggregation", { path: source.label });
  }
  return source.label;
}

export default function WidgetVisualEditor({
  outputFormat,
  widgetName,
  primaryIndex,
  timeField,
  mappingText,
  onMappingTextChange,
  onTimeFieldChange,
  tableAnalysis,
  probeLoading,
  probeError,
  onRefreshProbe,
  disabled,
  adaptorSource = false,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.widgetEditor;

  const chartMapping = useMemo(() => {
    try {
      return parseWidgetChartMapping(JSON.parse(mappingText));
    } catch {
      return buildWidgetChartMapping("bar", "vertical", widgetName);
    }
  }, [mappingText, widgetName]);

  const tableMapping = useMemo(() => {
    try {
      return parseTableMapping(JSON.parse(mappingText));
    } catch {
      return parseTableMapping({});
    }
  }, [mappingText]);

  const resultSources = tableAnalysis?.sources ?? [];
  const selectedResultSourceId = useMemo(
    () => (tableAnalysis ? resolveResultSourceId(tableAnalysis, tableMapping.rowSource) : null),
    [tableAnalysis, tableMapping.rowSource]
  );

  const selectedResultSource = useMemo(
    () => resultSources.find((source) => source.id === selectedResultSourceId) ?? null,
    [resultSources, selectedResultSourceId]
  );

  const columnFieldOptions = useMemo<TableResultFieldOption[]>(() => {
    if (!tableAnalysis || !selectedResultSourceId) {
      return [];
    }
    return tableAnalysis.fieldsBySource[selectedResultSourceId] ?? [];
  }, [tableAnalysis, selectedResultSourceId]);

  const onChartMappingChange = (next: WidgetChartMapping) => {
    onMappingTextChange(mappingToJson(next));
  };

  const updateTableMapping = (nextColumns: TableColumnMapping[], rowSource?: string) => {
    const resolvedRowSource =
      rowSource ??
      tableMapping.rowSource ??
      (selectedResultSource?.kind === "hits" ? resultSourceToRowSource(selectedResultSource) : undefined);
    onMappingTextChange(
      buildTableMappingJson({
        columns: nextColumns,
        rowSource: resolvedRowSource,
      })
    );
  };

  const onResultSourceChange = (source: TableResultSource | null) => {
    if (!source) {
      return;
    }
    const validOptions = tableAnalysis?.fieldsBySource[source.id] ?? [];
    if (outputFormat === "table") {
      const nextRowSource = resultSourceToRowSource(source);
      const nextColumns = tableMapping.columns.filter((column) => {
        const matches = findTableResultFieldOptions(column, validOptions);
        const expectedCount = column.sources?.length ?? 1;
        return matches.length === expectedCount;
      });
      updateTableMapping(nextColumns, nextRowSource);
      return;
    }
    if (outputFormat === "echarts") {
      const nextSlices = (chartMapping.slices ?? []).filter((slice) => {
        const matches = findTableResultFieldOptions(slice, validOptions);
        return matches.length === 1;
      });
      onChartMappingChange({ ...chartMapping, slices: nextSlices });
    }
  };

  const addColumnFromOption = (opt: TableResultFieldOption) => {
    const col = fieldOptionToColumn(opt);
    if (
      tableMapping.columns.some(
        (c) =>
          c.field === col.field &&
          c.path === col.path &&
          JSON.stringify(c.sources ?? []) === JSON.stringify(col.sources ?? [])
      )
    ) {
      return;
    }
    updateTableMapping([...tableMapping.columns, col], tableMapping.rowSource);
  };

  return (
    <Stack spacing={2}>
      {onTimeFieldChange && !adaptorSource && (
        <AppFormGrid alignItems="stretch">
          <AppFormField size={GRID_FULL}>
            <IndexTimeFieldAutocomplete
              index={primaryIndex}
              value={timeField}
              onChange={onTimeFieldChange}
              disabled={disabled}
              helperText={t("visualEditor.hintsFor", { index: primaryIndex || "—" })}
              showReload
              sx={chartOptionInputSx}
            />
          </AppFormField>
        </AppFormGrid>
      )}

      {outputFormat === "echarts" && (
        <Stack spacing={2}>
          <ChartEditorPanel mapping={chartMapping} onChange={onChartMappingChange} disabled={disabled} />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={styles.visualTableHeader}>
              <Typography variant="subtitle2">{t("visualEditor.chartSlices")}</Typography>
              {onRefreshProbe && (
                <AppButton
                  variant="outlined"
                  size="small"
                  startIcon={probeLoading ? <CircularProgress size={14} /> : <RefreshOutlinedIcon />}
                  onClick={onRefreshProbe}
                  disabled={disabled || probeLoading}
                >
                  {t("visualEditor.dslProbeRefresh")}
                </AppButton>
              )}
            </Stack>

            {probeLoading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={styles.visualProbeLoading}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t("visualEditor.dslProbeLoading")}
                </Typography>
              </Stack>
            )}

            {probeError && (
              <Alert severity="warning" sx={[styles.visualAlert, { mb: 1.5 }]}>
                {t("visualEditor.dslProbeFailed", { error: probeError })}
              </Alert>
            )}

            {!probeLoading && !probeError && resultSources.length === 0 && (
              <Alert severity="info" sx={[styles.visualAlert, { mb: 1.5 }]}>
                {t("visualEditor.dslProbeEmpty")}
              </Alert>
            )}

            {resultSources.length > 0 && (
              <AppFormGrid spacing="compact" alignItems="stretch" sx={styles.visualResultGrid}>
                <AppFormField size={GRID_FULL}>
                  <AppAutocomplete<TableResultSource>
                    label={t("visualEditor.resultSource")}
                    options={resultSources}
                    value={selectedResultSource}
                    onChange={(_, opt) => onResultSourceChange(opt)}
                    getOptionLabel={(source) => sourceLabel(source, t)}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    disableClearable
                    disabled={disabled}
                    helperText={t("visualEditor.chartResultSourceHint")}
                    sx={chartOptionInputSx}
                  />
                </AppFormField>
                {columnFieldOptions.length > 0 && (
                  <AppFormField size={GRID_FULL}>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      <Chip size="small" label={t("visualEditor.fieldCount", { count: columnFieldOptions.length })} />
                    </Stack>
                  </AppFormField>
                )}
              </AppFormGrid>
            )}

            <ChartSlicesEditor
              slices={chartMapping.slices ?? []}
              fieldOptions={columnFieldOptions}
              disabled={disabled}
              onChange={(slices) => onChartMappingChange({ ...chartMapping, slices })}
            />
          </Box>
        </Stack>
      )}

      {outputFormat === "table" && (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={styles.visualTableHeader}>
            <Typography variant="subtitle2">{t("visualEditor.tableColumns")}</Typography>
            {onRefreshProbe && (
              <AppButton
                variant="outlined"
                size="small"
                startIcon={probeLoading ? <CircularProgress size={14} /> : <RefreshOutlinedIcon />}
                onClick={onRefreshProbe}
                disabled={disabled || probeLoading}
              >
                {t("visualEditor.dslProbeRefresh")}
              </AppButton>
            )}
          </Stack>

          {probeLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={styles.visualProbeLoading}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {t("visualEditor.dslProbeLoading")}
              </Typography>
            </Stack>
          )}

          {probeError && (
            <Alert severity="warning" sx={[styles.visualAlert, { mb: 1.5 }]}>
              {t("visualEditor.dslProbeFailed", { error: probeError })}
            </Alert>
          )}

          {!probeLoading && !probeError && resultSources.length === 0 && (
            <Alert severity="info" sx={[styles.visualAlert, { mb: 1.5 }]}>
              {t("visualEditor.dslProbeEmpty")}
            </Alert>
          )}

          {resultSources.length > 0 && (
            <AppFormGrid spacing="compact" alignItems="stretch" sx={styles.visualResultGrid}>
              <AppFormField size={GRID_FULL}>
                <AppAutocomplete<TableResultSource>
                  label={t("visualEditor.resultSource")}
                  options={resultSources}
                  value={selectedResultSource}
                  onChange={(_, opt) => onResultSourceChange(opt)}
                  getOptionLabel={(source) => sourceLabel(source, t)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disableClearable
                  disabled={disabled}
                  helperText={t("visualEditor.resultSourceHint")}
                  sx={chartOptionInputSx}
                />
              </AppFormField>
              {columnFieldOptions.length > 0 && (
                <AppFormField size={GRID_FULL}>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    <Chip size="small" label={t("visualEditor.fieldCount", { count: columnFieldOptions.length })} />
                  </Stack>
                </AppFormField>
              )}
            </AppFormGrid>
          )}

          <TableColumnsEditor
            columns={tableMapping.columns}
            fieldOptions={columnFieldOptions}
            disabled={disabled}
            onChange={(nextColumns) => updateTableMapping(nextColumns, tableMapping.rowSource)}
          />

          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={styles.visualAddColumnRow}>
            <AppButton
              variant="outlined"
              size="small"
              startIcon={<AddOutlinedIcon />}
              disabled={disabled}
              onClick={() => {
                const used = new Set(
                  tableMapping.columns.map((c) => `${c.field}:${c.path}`)
                );
                const next = columnFieldOptions.find(
                  (o) => !used.has(`${o.field}:${o.path}`)
                );
                if (next) {
                  addColumnFromOption(next);
                  return;
                }
                const index = tableMapping.columns.length;
                updateTableMapping(
                  [
                    ...tableMapping.columns,
                    createCustomTableColumn(
                      index,
                      t("visualEditor.customColumnDefaultHeader", { index: index + 1 })
                    ),
                  ],
                  tableMapping.rowSource
                );
              }}
            >
              {t("visualEditor.addColumn")}
            </AppButton>
            <AppButton
              variant="outlined"
              size="small"
              startIcon={<AddOutlinedIcon />}
              disabled={disabled}
              onClick={() => {
                const index = tableMapping.columns.length;
                updateTableMapping(
                  [
                    ...tableMapping.columns,
                    createCustomTableColumn(
                      index,
                      t("visualEditor.customColumnDefaultHeader", { index: index + 1 })
                    ),
                  ],
                  tableMapping.rowSource
                );
              }}
            >
              {t("visualEditor.addCustomColumn")}
            </AppButton>
          </Stack>
        </Box>
      )}

      {(outputFormat === "text" || outputFormat === "html") && (
        <Typography variant="body2" color="text.secondary">
          {t("visualEditor.textFormatHint")}
        </Typography>
      )}
    </Stack>
  );
}
